import React from 'react';
import { createLDReactProvider } from '@launchdarkly/react-sdk';

import { buildLaunchDarklyContext } from './launchDarklyContext';
import { getLaunchDarklyClientSideId, type LaunchDarklyApp } from './constants';

export interface LaunchDarklyProviderProps {
  app: LaunchDarklyApp;
  children: React.ReactNode;
}

const createProvider = (clientSideId: string, app: LaunchDarklyApp) =>
  createLDReactProvider(clientSideId, buildLaunchDarklyContext(app), {
    ldOptions: {
      streaming: true,
      useCamelCaseFlagKeys: false,
    },
  });

const providerCache = new Map<string, ReturnType<typeof createLDReactProvider>>();

const getOrCreateProvider = (clientSideId: string, app: LaunchDarklyApp) => {
  const cacheKey = `${clientSideId}:${app}`;

  if (!providerCache.has(cacheKey)) {
    providerCache.set(cacheKey, createProvider(clientSideId, app));
  }

  return providerCache.get(cacheKey)!;
};

export const LaunchDarklyProvider = ({ app, children }: LaunchDarklyProviderProps) => {
  const clientSideId = getLaunchDarklyClientSideId();

  if (!clientSideId) {
    return <>{children}</>;
  }

  const LDProvider = getOrCreateProvider(clientSideId, app);

  return <LDProvider>{children}</LDProvider>;
};
